import { Column } from "@stenajs-webui/core";
import { ModalBody } from "@stenajs-webui/modal";
import { useCallback, useState } from "react";

import {
  type InformationMessage,
  InformationMessageType,
} from "../../../../../common-ui/information-message/informationMessageUtils";
import { useAppNavigate } from "../../../../../common/hooks/useAppNavigate";
import { modalRouteCreators } from "../../../../../common/routes/AppRoutes";
import { revertChangesUpdateForm } from "../../../../../services/update-form/updateFormActions";
import { useAppDispatch } from "../../../../../store/appDispatch";
import { BookingActionMenuButton } from "../BookingActionMenuButton";
import { BookingDetailsHeader } from "../BookingDetailsHeader";
import type { BookingDetailsResultProps } from "../BookingDetailsModalLoader";
import { BookingDetailsTabView } from "../tabs/BookingDetailsTabView";
import type { BookingDetailsTabs } from "../tabs/types";
import { BookingDetailsViewContent } from "./BookingDetailsViewContent";
import { BookingDetailsViewFooter } from "./BookingDetailsViewFooter";

export interface BookingDetailsViewContainerProps extends BookingDetailsResultProps {}

export function BookingDetailsViewContainer({
  booking,
  onRequestClose,
}: BookingDetailsViewContainerProps) {
  const { bookingNo } = booking;
  const [detailsTab, setDetailsTab] = useState<BookingDetailsTabs>("details");
  const dispatch = useAppDispatch();
  const [cancelStatusMessages, setCancelStatusMessages] = useState<
    InformationMessage[]
  >([]);
  const { replace } = useAppNavigate();

  const navigateToEdit = () => {
    dispatch(revertChangesUpdateForm({ formId: "Details", bookingNo }));
    setCancelStatusMessages([]);
    replace(modalRouteCreators.bookingDetailsEdit({ bookingNo }));
  };

  const onCancelFail = useCallback(() => {
    setCancelStatusMessages([
      {
        message: `Failed to cancel booking (${bookingNo})`,
        type: InformationMessageType.HardWarning,
      },
    ]);
  }, [bookingNo, setCancelStatusMessages]);

  return (
    <>
      <ModalBody>
        <BookingDetailsHeader
          className={"t_booking_details_view_header"}
          booking={booking}
          onRequestClose={onRequestClose}
          bookingActions={
            <Column width={"100%"} alignItems={["stretch", null, "flex-end"]}>
              <BookingActionMenuButton booking={booking} />
            </Column>
          }
        />
        {booking.gatePhotos &&
        (booking.gatePhotos.inboundHasCapability ||
          booking.gatePhotos.outboundHasCapability) ? (
          <BookingDetailsTabView
            gatePhotos={booking.gatePhotos}
            booking={booking}
            tab={detailsTab}
            setTab={(tab: BookingDetailsTabs) => {
              setDetailsTab(tab);
            }}
          >
            <BookingDetailsViewContent
              booking={booking}
              cancelStatusMessages={cancelStatusMessages}
            />
          </BookingDetailsTabView>
        ) : (
          <BookingDetailsViewContent
            booking={booking}
            cancelStatusMessages={cancelStatusMessages}
          />
        )}
      </ModalBody>
      {detailsTab !== "gate-photos" && (
        <BookingDetailsViewFooter
          booking={booking}
          onCancelFail={onCancelFail}
          onClickEdit={navigateToEdit}
        />
      )}
    </>
  );
}
